/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css } from 'aphrodite';
import { Markup } from '@blend/consumer-components';

import { ActionButton, ProgressBar } from '@blend/consumer-components';

import { ModalProps, ModalState, UploadResponse, WithUploads } from '../types';
import { UploadItem } from '../UploadItem/UploadItem';
import FileUploader from '../FileUploader';
import { useConsumerTheme } from '../../../../../hooks/useConsumerTheme';
import styles from './style';
import { AlertBox } from '@components/AlertBox/AlertBox';
import { DocUploadIcon } from '@components/DocUploadIcon/DocUploadIcon';

export interface MobileModalProps extends WithUploads, ModalProps, ModalState {
  canSubmit: boolean;
  multiple?: boolean;
  disabled: boolean;
  imageName?: string;
  uploadFile: (file: File) => Promise<UploadResponse>;
}

export const MobileModal: React.FC<MobileModalProps> = ({ ...props }) => {
  const { progress } = props;
  const {
    appConfig: { colors },
  } = useConsumerTheme();

  const renderContent = () => {
    const { message, errorMessage, title, imageName } = props;
    return (
      <div className={css(styles.content)}>
        <Markup element="p" className={css(styles.description)} content={message} />
        {!!errorMessage && (
          <AlertBox type="error" icon="error">
            {errorMessage}
          </AlertBox>
        )}
        <div className={css(styles.inset)}>
          <span className={css(styles.titleContainer)}>
            {imageName && (
              <div className={css(styles.iconWrapper)}>
                <DocUploadIcon imageName={imageName} size="small" />
              </div>
            )}
            <h2 className={css(styles.title)}>{title}</h2>
          </span>
          {renderUploadList()}
        </div>
      </div>
    );
  };

  const renderUploadList = () => {
    const { disabled, fileIds, uploads, onRemove, isSubmitting } = props;
    const isUploadDisabled = isSubmitting || disabled;
    return (
      fileIds.length > 0 && (
        <ul className={css(styles.uploadList)}>
          {fileIds.map(fileId => (
            <li key={fileId} className={css(styles.uploadItem)}>
              <UploadItem
                upload={uploads[fileId]}
                onRemove={() => onRemove(fileId, uploads[fileId])}
                disabled={isUploadDisabled}
                borderless
              />
            </li>
          ))}
        </ul>
      )
    );
  };

  const renderControls = () => {
    const { disabled, uploadFile, onUpload, isSubmitting, canSubmit, onSubmit, multiple = true } = props;
    const isSubmitDisabled = !canSubmit || disabled;
    const isUploadDisabled = isSubmitting || disabled;
    return (
      <div className={css(styles.controls)}>
        <FileUploader
          id="DocUploaderModal-input"
          multiple={multiple}
          disabled={isUploadDisabled}
          uploadFile={uploadFile}
          onUploadChange={onUpload}
          customAllowedFileTypes={['pdf']}
        >
          <ActionButton data-dd-privacy="allow" fake option="secondary" color={colors.action} font="UntitledSans">
            Upload another file
          </ActionButton>
        </FileUploader>
        <div className={css(styles.submitButton)}>
          <ActionButton
            data-dd-privacy="allow"
            type="submit"
            option="primary"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            color={colors.action}
            font="UntitledSans"
          >
            I'm done
          </ActionButton>
        </div>
      </div>
    );
  };

  return (
    <div className={css(styles.DocUploaderMobileModal)}>
      {!!progress && (
        <div className={css(styles.submitProgress)}>
          <ProgressBar
            description="Submitting documents"
            backgroundType="none"
            value={progress}
            actionColor={colors.action}
          />
        </div>
      )}
      <div className={css(styles.contentContainer)}>
        {renderContent()}
        {renderControls()}
      </div>
    </div>
  );
};
